import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, MapPin } from "lucide-react";
import { projects, PANORAMA_360 } from "@/data/site";
import Project360Viewer from "@/components/Project360Viewer";

const EASE = [0.22, 1, 0.36, 1];

export default function ProjectDetail() {
    const { slug } = useParams();
    const project = projects.find((p) => p.slug === slug);
    if (!project) return <Navigate to="/projects" replace />;

    const idx = projects.findIndex((p) => p.slug === slug);
    const next = projects[(idx + 1) % projects.length];

    return (
        <div data-testid="page-project-detail">
            {/* Hero */}
            <section className="relative min-h-[100svh] bg-forest-950 overflow-hidden">
                <img
                    src={project.cover}
                    alt={project.name}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-forest-950/60 via-forest-950/25 to-forest-950" />

                <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 pt-40 md:pt-44 pb-20 md:pb-24 min-h-[100svh] flex flex-col justify-end">
                    <Link
                        to="/projects"
                        data-testid="back-to-projects"
                        className="inline-flex items-center gap-3 font-mono-mini text-[10px] tracking-[0.32em] uppercase text-ivory/70 hover:text-lime mb-10 hover-underline w-fit"
                    >
                        <ArrowLeft size={14} /> All projects
                    </Link>

                    <div className="flex items-center gap-4 mb-6">
                        <span className="h-px w-10 bg-lime" />
                        <span className="font-mono-mini text-[10px] tracking-[0.42em] uppercase text-lime">
                            {project.status} · {project.type}
                        </span>
                    </div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, ease: EASE }}
                        className="font-serif-display leading-[0.94] tracking-tighter text-ivory text-[3.2rem] md:text-[6rem] lg:text-[7.5rem] max-w-[14ch]"
                    >
                        {project.name}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
                        className="mt-8 font-serif-display italic text-ivory/85 text-2xl md:text-3xl max-w-xl"
                    >
                        {project.tagline}
                    </motion.p>
                </div>
            </section>

            {/* Meta strip */}
            <section className="bg-forest-950 border-y border-white/10">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-2 md:grid-cols-4">
                    {[
                        { label: "Location", value: project.location, Icon: MapPin },
                        { label: "Programme", value: project.type },
                        { label: "Configuration", value: project.units },
                        { label: "Year", value: project.year },
                    ].map((m, i) => (
                        <div
                            key={m.label}
                            className={`py-8 md:py-10 px-4 md:px-8 ${i > 0 ? "md:border-l border-white/10" : ""} ${i > 0 && i % 2 === 0 ? "border-t md:border-t-0" : ""} ${i === 1 ? "border-t md:border-t-0" : ""}`}
                        >
                            <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime/80 mb-3">
                                {m.label}
                            </div>
                            <div className="font-serif-display text-xl md:text-[1.35rem] text-ivory leading-tight">
                                {m.value}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Summary + gallery */}
            <section className="bg-forest-900 py-24 md:py-32">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime mb-4">
                            The idea
                        </div>
                        <h2 className="font-serif-display text-3xl md:text-4xl leading-tight text-ivory">
                            A slow, deliberate response to the site.
                        </h2>
                    </div>
                    <div className="md:col-span-7 md:col-start-6 text-ivory/80 leading-relaxed text-[1.05rem] space-y-4">
                        <p>{project.summary}</p>
                        <p>
                            Every surface — the concrete, the stone, the joinery — has been drawn
                            at full-scale in our Bengaluru studio, and mocked up on site before
                            production began.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-forest-950 py-16 md:py-24">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-6">
                    {project.gallery.map((src, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, ease: EASE, delay: i * 0.06 }}
                            data-testid={`gallery-image-${i}`}
                            className={`${i === 0 ? "md:col-span-8" : i === 1 ? "md:col-span-4" : "md:col-span-12"} overflow-hidden bg-forest-800 aspect-[16/10]`}
                        >
                            <img
                                src={src}
                                alt={`${project.name} gallery ${i + 1}`}
                                loading="lazy"
                                className="h-full w-full object-cover"
                            />
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* 360° tour */}
            <section className="bg-forest-950 pb-16 md:pb-24">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                    <div className="flex items-center gap-4 mb-8">
                        <span className="h-px w-10 bg-lime" />
                        <span className="font-mono-mini text-[10px] tracking-[0.42em] uppercase text-lime">
                            Immersive tour · Beta
                        </span>
                    </div>
                    <Project360Viewer image={PANORAMA_360} projectName={project.name} />
                </div>
            </section>

            {/* Next project */}
            <section className="bg-forest-900 py-20 md:py-28 border-t border-white/10">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                    <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime mb-6">
                        Next project
                    </div>
                    <Link
                        to={`/projects/${next.slug}`}
                        data-testid="next-project-link"
                        className="group flex flex-col md:flex-row md:items-center md:justify-between gap-6"
                    >
                        <h3 className="font-serif-display text-4xl md:text-[5.5rem] leading-none tracking-tight text-ivory group-hover:text-lime transition-colors">
                            {next.name}
                        </h3>
                        <span className="h-14 w-14 md:h-16 md:w-16 bg-lime text-forest-900 flex items-center justify-center group-hover:rotate-45 transition-transform duration-500">
                            <ArrowUpRight size={22} strokeWidth={1.5} />
                        </span>
                    </Link>
                </div>
            </section>
        </div>
    );
}
